import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import SEOHead from '@/components/SEOHead';
import { PAGE_SEO_CONFIG } from '@/utils/seoUtils';

const VirtualHostTerms: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.style.scrollBehavior = 'smooth';

    fetch('/legal/virtual-host-terms.md')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load terms');
        }
        return response.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading VH terms:', err);
        setError('Failed to load Virtual Host Terms of Service. Please try again later.');
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      <SEOHead
        title={PAGE_SEO_CONFIG.virtualHostTerms.title}
        description={PAGE_SEO_CONFIG.virtualHostTerms.description}
        keywords={PAGE_SEO_CONFIG.virtualHostTerms.keywords?.join(', ')}
      />
      {/* Header */}
      <div className="bg-blue-600 border-b border-blue-700 sticky top-0 z-10 shadow-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center text-sm text-white hover:text-blue-100 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <img
              src="/dineai-logo.png"
              alt="Dine AI"
              className="h-10"
            />
          </div>
        </div>
      </div>

      {/* Terms Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && content && (
            <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                  rehypeSlug,
                  [rehypeAutolinkHeadings, { behavior: 'wrap' }]
                ]}
                components={{
                  h1: ({children}) => (
                    <h1 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                      {children}
                    </h1>
                  ),
                  h2: ({children, ...props}) => (
                    <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4" {...props}>
                      {children}
                    </h2>
                  ),
                  h3: ({children, ...props}) => (
                    <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3" {...props}>
                      {children}
                    </h3>
                  ),
                  p: ({children}) => (
                    <p className="mb-4 text-justify">
                      {children}
                    </p>
                  ),
                  ul: ({children}) => (
                    <ul className="list-disc ml-6 mb-4">
                      {children}
                    </ul>
                  ),
                  li: ({children}) => (
                    <li className="mb-2">
                      {children}
                    </li>
                  ),
                  a: ({href, children}) => (
                    <a
                      href={href}
                      className="text-blue-600 hover:text-blue-800 underline"
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    >
                      {children}
                    </a>
                  ),
                  blockquote: ({children}) => (
                    <div className="bg-gray-50 rounded-lg p-6 mb-8">
                      {children}
                    </div>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-blue-600 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-sm text-white">
                Need help? Contact us at{' '}
                <a href="mailto:support@dineai.ai" className="text-blue-100 hover:text-white underline">
                  support@dineai.ai
                </a>
              </p>
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-sm text-blue-100 hover:text-white">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-blue-100 hover:text-white">
                Terms of Service
              </Link>
              <Link to="/virtual-host-terms" className="text-sm text-white font-medium">
                Virtual Host Terms
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualHostTerms;
